import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Col, Empty, Row, Switch, Tag, Tooltip, Typography } from 'antd';
import { MemoryStick, RefreshCw } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { HeapSpacesTable } from './components/HeapSpacesTable';
import { LeakVerdict } from './components/LeakVerdict';
import { type MemorySample, MemoryTrendChart } from './components/MemoryTrendChart';
import { MemoryVitals } from './components/MemoryVitals';
import { SnapshotInventory } from './components/SnapshotInventory';
import { SubsystemHealth } from './components/SubsystemHealth';

export const Route = createFileRoute('/admin-console/monitoring/memory')({
    staticData: {
        permissions: ['admin_system:read'],
        breadcrumb: {
            title: 'Memory diagnostics',
        },
        ui: {
            icon: MemoryStick,
            title: 'Memory diagnostics',
            subtitle: 'Live process memory, leak triage and heap snapshots',
            menuDescription: 'Triage server memory leaks',
        },
    },
    component: MemoryDiagnosticsPage,
});

/** Refresh cadence for the live diagnostics poll, in ms. */
const REFRESH_MS = 5000;
/** Cap on the client-side rolling sample history fed to the trend chart. */
const MAX_SAMPLES = 120;

function MemoryDiagnosticsPage() {
    const { hasAccess, canEdit } = useAdminModulePermissions('system');
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [history, setHistory] = useState<MemorySample[]>([]);

    const diagnosticsQuery = trpc.admin.monitor.getMemoryDiagnostics.useQuery(undefined, {
        enabled: hasAccess,
        refetchInterval: autoRefresh ? REFRESH_MS : false,
    });
    const snapshotsQuery = trpc.admin.monitor.listHeapSnapshots.useQuery(undefined, {
        enabled: hasAccess,
        refetchInterval: autoRefresh ? REFRESH_MS * 4 : false,
    });
    const subsystemsQuery = trpc.admin.monitor.checkSubsystems.useQuery(undefined, {
        enabled: hasAccess,
        refetchInterval: autoRefresh ? REFRESH_MS * 4 : false,
    });

    const diagnostics = diagnosticsQuery.data;

    useEffect(() => {
        if (!diagnostics) return;
        setHistory((prev) => {
            const workerHistory = prev.at(-1)?.pmInstanceId === diagnostics.pmInstanceId ? prev : [];

            return [
                ...workerHistory,
                {
                    time: diagnostics.capturedAt,
                    pmInstanceId: diagnostics.pmInstanceId,
                    rssMB: diagnostics.process.rssMB,
                    heapUsedMB: diagnostics.process.heapUsedMB,
                    rssMinusHeapTotalMB: diagnostics.process.rssMinusHeapTotalMB,
                },
            ].slice(-MAX_SAMPLES);
        });
    }, [diagnostics]);

    const refetchAll = () => {
        diagnosticsQuery.refetch();
        snapshotsQuery.refetch();
        subsystemsQuery.refetch();
    };

    if (!hasAccess) {
        return (
            <PageController route={Route}>
                <div className="flex min-h-[400px] items-center justify-center">
                    <Empty
                        image={<MemoryStick size={64} className="mx-auto text-quaternary-foreground" />}
                        description="You don't have permission to view memory diagnostics."
                    />
                </div>
            </PageController>
        );
    }

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={diagnosticsQuery.error}>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        {diagnostics && (
                            <Tooltip title="The PM2 worker that answered this request. In cluster mode metrics and manual capture reflect this worker only.">
                                <Tag color="blue" variant="outlined">
                                    worker pm{diagnostics.pmInstanceId}
                                </Tag>
                            </Tooltip>
                        )}
                        {diagnostics && (
                            <Typography.Text type="secondary" className="text-xs">
                                uptime {Math.round(diagnostics.uptimeSeconds / 60)} min · updated{' '}
                                {DateTime.fromISO(diagnostics.capturedAt).toFormat('HH:mm:ss')}
                            </Typography.Text>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="flex items-center gap-2">
                            <Switch
                                size="small"
                                checked={autoRefresh}
                                onChange={setAutoRefresh}
                                aria-label={`Auto-refresh memory diagnostics every ${REFRESH_MS / 1000} seconds`}
                            />
                            <Typography.Text type="secondary" className="text-xs">
                                Auto-refresh ({REFRESH_MS / 1000}s)
                            </Typography.Text>
                        </span>
                        <Button icon={<RefreshCw />} loading={diagnosticsQuery.isRefetching} onClick={refetchAll}>
                            Refresh
                        </Button>
                    </div>
                </div>

                {diagnostics && (
                    <div className="mb-4">
                        <LeakVerdict verdict={diagnostics.verdict} />
                    </div>
                )}

                {diagnostics && (
                    <div className="mb-4">
                        <MemoryVitals diagnostics={diagnostics} />
                    </div>
                )}

                <div className="mb-4">
                    <MemoryTrendChart history={history} />
                </div>

                <Row gutter={[16, 16]} className="mb-4">
                    <Col xs={24} lg={12}>
                        {diagnostics && <HeapSpacesTable heapSpaces={diagnostics.heapSpaces} />}
                    </Col>
                    <Col xs={24} lg={12}>
                        <SubsystemHealth data={subsystemsQuery.data} isLoading={subsystemsQuery.isLoading} />
                    </Col>
                </Row>

                <SnapshotInventory
                    inventory={snapshotsQuery.data}
                    isLoading={snapshotsQuery.isLoading}
                    canCapture={canEdit}
                    onRefetch={() => snapshotsQuery.refetch()}
                />
            </TRPCErrorBoundary>
        </PageController>
    );
}
